import React from 'react';
import { useNavigate } from 'react-router-dom';
import { get, isEmpty, sortBy } from 'lodash';
import { useReport, useReportScore } from 'src/queries/reports';
import { useCompany } from 'src/queries/company';
import { appUtils } from 'src/components';
import { CategoryNewReport } from 'src/pagesDashboard/NewUserReport/components';
import commonQuestions from 'common/commonQuestions';
import COMMON_CONSTANTS from 'common/commonConstants';

const { REPORT_ERRORS } = COMMON_CONSTANTS;

const ReportCategoriesSection = ({
  reportId,
  userId,
  isEditorOpenByDefault,
  viewOnly = false
}) => {
  const navigate = useNavigate();

  const { data: report } = useReport(reportId, {
    staleTime: Infinity,
    cacheTime: Infinity
  });

  const { data: company } = useCompany({
    staleTime: Infinity,
    cacheTime: Infinity
  });

  const {
    data: reportScore,
    isFetching: isFetchingReportScore,
    isError: isErrorReportScore,
    error: reportScoreError
  } = useReportScore(
    reportId,
    { include: { previousReportScore: true } },
    { staleTime: Infinity, cacheTime: Infinity }
  );

  if (isErrorReportScore) {
    const errorMessage = get(reportScoreError, 'response.data.message', '');

    if (errorMessage === REPORT_ERRORS.REPORT_NOT_FOUND) {
      navigate(appUtils.getDashRoute(userId, 'reports'));
    }
  }

  const isFetching = isFetchingReportScore;
  const isError = isErrorReportScore;
  const isReady = !isFetching && !isError;
  if (!isReady) return <div className='h-full' />;

  const companyQuestions = get(company, 'questions', {});
  const categories = [];
  const bundledCategories = get(reportScore, 'bundledCategories', []);

  bundledCategories.forEach((c) => {
    const categoryObj = commonQuestions.getCategory(
      c.id,
      companyQuestions.CATEGORIES
    );
    if (!categoryObj) {
      return;
    }

    categories.push({
      id: c.id.toString(),
      avgScore: c.avgScore,
      reviewCount: c.reviewCount,
      label: categoryObj.label,
      questions: categoryObj.questions,
      timestamp: categoryObj.timestamp,
      lastUpdated: categoryObj.lastUpdated,
      status: categoryObj.status,
      roleLabels: c.roleIds
        .map((roleId) => {
          const role = commonQuestions.getRoleById(roleId, companyQuestions);
          return role.label;
        })
        .join(',')
    });
  });

  const { previousReportScore } = reportScore;

  const previousCategories = [];
  let previousBundledCategories = [];
  if (!isEmpty(previousReportScore)) {
    previousBundledCategories = get(
      previousReportScore,
      'bundledCategories',
      []
    );

    previousBundledCategories.forEach((c) => {
      const categoryObj = commonQuestions.getCategory(
        c.id,
        companyQuestions.CATEGORIES
      );
      if (!categoryObj) {
        return;
      }

      previousCategories.push({
        id: c.id.toString(),
        avgScore: c.avgScore,
        reviewCount: c.reviewCount,
        label: categoryObj.label,
        questions: categoryObj.questions,
        timestamp: categoryObj.timestamp,
        lastUpdated: categoryObj.lastUpdated,
        status: categoryObj.status,
        roleLabels: c.roleIds
          .map((roleId) => {
            const role = commonQuestions.getRoleById(roleId, companyQuestions);
            return role.label;
          })
          .join(',')
      });
    });
  }

  const categoriesToRender = [];
  categories.forEach((c) => {
    if (!categoriesToRender.find((ctr) => ctr.id === c.id)) {
      const previousCategory = previousCategories.find((pc) => pc.id === c.id);
      if (previousCategory) {
        categoriesToRender.push({ ...c, previousCategory });
      } else {
        categoriesToRender.push(c);
      }
    }
  });

  return (
    <div className='mt-5'>
      {categoriesToRender.length ? (
        sortBy(categoriesToRender, 'label').map((category) => (
          <CategoryNewReport
            category={category}
            previousCategory={category.previousCategory}
            reportId={reportId}
            previousReport={report.previousReport}
            userId={userId}
            roleLabels={category.roleLabels}
            isEditorOpenByDefault={isEditorOpenByDefault}
            viewOnly={viewOnly}
            start={report.start}
            end={report.end}
            key={category.id}
          />
        ))
      ) : (
        <div className='flex justify-center mt-10'>
          <p className='text-lg'>
            No category data available for this time range
          </p>
        </div>
      )}
    </div>
  );
};

export default ReportCategoriesSection;
